import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import can from '~/helpers/can';
import useAuth from '~/composables/useAuth';

/**
 * Offers methods to check user permissions
 * @returns {any}
 */
export default function usePermission() {
  const { userAccessCode } = useAuth();

  // List of roles (you can initialize if needed)
  const roles = [];

  /**
   * Tells if a user access code (administrator, manager...) can perform an action on a resource.
   * Actions: C=8, R=4, U=2, D=1, V=16 (View UI / Visible en UI)
   * @param action
   * @param resource
   * @returns {*|boolean}
   */
  function canUser(action, resource) {
    return can(userAccessCode.value, action, resource);
  }

  /**
   * Checks if a resource is visible in the UI (View bit = 16)
   * @param resource
   * @returns {*|boolean}
   */
  function canView(resource) {
    return canUser(16, resource);
  }

  /**
   * Can the current user perform an action on another user
   * @param action
   * @param targetUserAccessCode
   * @returns {boolean}
   */
  function canCurrentUserPerformAction(action, targetUserAccessCode) {
    const roleLevel = window.keyprodpermissions.roleLevels[userAccessCode.value];
    if (roleLevel) {
      const allowedActions = roleLevel[targetUserAccessCode];
      if (allowedActions) {
        // eslint-disable-next-line no-bitwise
        return (allowedActions & action) === action;
      }
    }

    return false;
  }

  /**
   * Can the current user access the personalization settings
   */
  const canAccessPersonalization = computed(() => canUser(2, 'Themes'));

  /**
   * Can the current user edit the application settings
   */
  const canEditSettings = computed(() => {
    if (userAccessCode.value) {
      return canUser(2, 'Devices') && canUser(2, 'MachineGroups');
    }
    return false;
  });

  /**
   * Can the current user display the application settings
   */
  const canDisplaySettings = computed(() => {
    if (userAccessCode.value) {
      return canUser(4, 'Settings') && canUser(2, 'MachineGroups');
    }

    return false;
  });

  /**
   * Can the current user create an event?
   * It's used to hide/display the create event button in the UI.
   */
  const canCreateEvent = computed(() => userAccessCode.value && userAccessCode.value !== 'simplified_operator');

  const { t } = useI18n();

  /**
   * Get the friendly name of an access
   */
  function getAccessFriendlyName(internalName) {
    switch (internalName) {
      case 'administrator':
        return t('user.access_administrator');
      case 'manager':
        return t('user.access_manager');
      case 'operator':
        return t('user.access_operator');
      case 'simplified_operator':
        return t('user.access_simplified_operator');
      default:
        return '';
    }
  }

  return {
    canAccessPersonalization,
    canCreateEvent,
    canCurrentUserPerformAction,
    canDisplaySettings,
    canEditSettings,
    canUser,
    canView,
    getAccessFriendlyName,
    roles,
  };
}
